/**
 * Available LLM models for prompt configuration.
 * These must match the models defined in the backend (services/libs/models/src/llm_model.rs)
 */

/**
 * Whether we stand behind a model or are still measuring it.
 *
 * `approved` — in use, quality understood, safe to point a client's prompts at.
 * `trial` — wired up and callable, but not yet signed off. Cost and classification quality
 * are still being established (EN-375), so a client-facing prompt should not rely on one.
 *
 * This is a judgement about our confidence, not about the model's capability, so it cannot
 * be derived from anything the provider tells us — it has to be stated here.
 */
export type LlmModelStatus = 'approved' | 'trial'

export interface LlmModelInfo {
    id: string
    displayName: string
    isDefault: boolean
    canClassifyImages: boolean
    status: LlmModelStatus
    /**
     * True for a model that answers yes/no with a probability and produces no text. It can
     * classify but cannot write a reply, so the reply-prompt form must not offer it.
     */
    classifyOnly?: true
}

/**
 * Static list of available LLM models.
 *
 * A hand-maintained mirror of the `LlmModel` enum in
 * services/libs/models/src/llm_model.rs, and the known cost of that: the enum carried ten
 * Bedrock models for a while before this list caught up, so half the trial fleet was
 * callable by the backend and invisible in the UI. Anything added to the enum has to be
 * added here too, with the same id and display name.
 *
 * SOMEDAY: replace this list with the `getAvailableLlmModels` query and delete it. The
 * backend already exposes the full set properly, resolved straight off `LlmModel::VARIANTS`,
 * with a generated `useGetAvailableLlmModelsQuery` hook ready to use in src/api/client.ts.
 * That makes drift impossible rather than merely documented, which is why it is the real
 * fix and this comment is not. It needs three things: `canClassifyImages` and `status` added
 * to the `LlmModelInfo` GraphQL type in
 * services/libs/graphql/src/schema/prompt/prompt_models.rs, a codegen run, and the three
 * selectors that consume `LLM_MODELS` moved onto the hook (ModelSelect.tsx,
 * PromptTestingPage.tsx and PromptDetailsForm.tsx via `REPLY_LLM_MODELS`, plus
 * `VISION_LLM_MODELS` for ImagePromptTestingPage.tsx). Held back
 * from this branch only because the codegen chain cannot run in CI and the generated schema
 * diff would bury the rest of the change.
 *
 * Ordered approved-first so the safe choice is the one nearest the top of every selector.
 */
export const LLM_MODELS: LlmModelInfo[] = [
    // ── Approved for use ──
    {
        id: 'gemini-3-pro',
        displayName: 'Google Gemini 3 Pro',
        isDefault: false,
        canClassifyImages: true,
        status: 'approved'
    },
    {
        id: 'gemini-2.5-flash-lite',
        displayName: 'Google Gemini 2.5 Flash Lite',
        isDefault: true,
        canClassifyImages: true,
        status: 'approved'
    },
    {
        id: 'gpt-5',
        displayName: 'OpenAI GPT-5',
        isDefault: false,
        canClassifyImages: false,
        status: 'approved'
    },
    {
        id: 'gpt-5-mini',
        displayName: 'OpenAI GPT-5 Mini',
        isDefault: false,
        canClassifyImages: false,
        status: 'approved'
    },
    {
        id: 'gpt-5-nano',
        displayName: 'OpenAI GPT-5 Nano',
        isDefault: false,
        canClassifyImages: false,
        status: 'approved'
    },

    // ── On trial (EN-375 / EN-397) ──
    // Gemini 3.1 Flash Lite is the forced migration target for 2.5 Flash Lite, but it is not
    // approved yet: profiles move over one at a time once classification quality is verified.
    {
        id: 'gemini-3.1-flash-lite',
        displayName: 'Google Gemini 3.1 Flash Lite',
        isDefault: false,
        canClassifyImages: true,
        status: 'trial'
    },

    // Amazon Nova, via Bedrock in eu-west-1. canClassifyImages is false for all of them: Lite
    // and above accept images at the provider, but our Bedrock client does not send them yet,
    // so offering them for image prompts would fail at classification time.
    {
        id: 'nova-micro',
        displayName: 'Amazon Nova Micro',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'nova-lite',
        displayName: 'Amazon Nova Lite',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'nova-2-lite',
        displayName: 'Amazon Nova 2 Lite',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'nova-pro',
        displayName: 'Amazon Nova Pro',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },

    // The rest of the Bedrock trial fleet. Same vision caveat as Nova — Claude Haiku 4.5
    // takes images at the provider, but not through our client.
    {
        id: 'claude-haiku-4.5',
        displayName: 'Anthropic Claude Haiku 4.5',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'gemma-3-27b',
        displayName: 'Google Gemma 3 27B',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'ministral-3-8b',
        displayName: 'Mistral Ministral 3 8B',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'gpt-oss-20b',
        displayName: 'OpenAI gpt-oss 20B',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },
    {
        id: 'gpt-oss-safeguard-20b',
        displayName: 'OpenAI gpt-oss-safeguard 20B',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial'
    },

    // TypeSafe Jev: a probability model, text only, no justification, US-hosted, so it is
    // enabled per client. Measured on the 17 Sep 2026 trial corpus: $0.0723 per 1k calls,
    // 85.6% recall of Gemini 3.1 (scripts/ad_hoc/2026-09-17_jev_trial/RESULTS.md).
    {
        id: 'jev-1.13',
        displayName: 'TypeSafe Jev 1.13',
        isDefault: false,
        canClassifyImages: false,
        status: 'trial',
        classifyOnly: true
    }
]

export const DEFAULT_LLM_MODEL = 'gemini-2.5-flash-lite'

/**
 * How a model is named wherever a user sees it.
 *
 * Trial models are prefixed rather than badged because three of the four model pickers are
 * native `<select>` elements, and an `<option>` cannot carry a chip, an icon or any styling
 * at all — the text is the only channel available. Putting the marker in the label keeps one
 * code path for every selector and the prompt table, so a trial model cannot be
 * distinguishable in one place and anonymous in another.
 */
export function getLlmModelLabel(model: LlmModelInfo): string {
    return model.status === 'trial' ? `[Trial] ${model.displayName}` : model.displayName
}

export const VISION_LLM_MODELS = LLM_MODELS.filter((m) => m.canClassifyImages)

/** Models a reply prompt may use: everything that can generate text. */
export const REPLY_LLM_MODELS = LLM_MODELS.filter((m) => !m.classifyOnly)

export const DEFAULT_VISION_LLM_MODEL =
    VISION_LLM_MODELS.find((m) => m.isDefault)?.id ?? VISION_LLM_MODELS[0]?.id ?? ''
